import { useState } from "react"
import { ArrowLeft, Layers } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"

import { LoadingState } from "@/components/loading-state"
import { PageHeader } from "@/components/page-header"
import { StatusBadge } from "@/components/status-badge"
import { Button } from "@/components/ui/button"
import { LotCompositionDialog } from "@/features/lots/components/lot-composition-dialog"
import { LotStatusActionCard } from "@/features/lots/components/lot-status-action-card"
import { QCSection } from "@/features/lots/components/qc-section"
import { useLot } from "@/features/lots/hooks/use-lots"
import { lotUsinageNumber } from "@/features/lots/lib/lot-math"
import { lotStatusTone } from "@/features/lots/lib/lot-status"
import { paths } from "@/routes/paths"

export function LotDetailPage() {
  const { t } = useTranslation("lots")
  const navigate = useNavigate()
  const { lotId } = useParams()
  const [compositionOpen, setCompositionOpen] = useState(false)
  // includeLoads pulls each source load's delivery + cooperative so the
  // composition dialog has everything it needs in one round-trip.
  const {
    data: lot,
    isLoading,
    isError,
  } = useLot(lotId, { includeLoads: true })

  if (isLoading) return <LoadingState />
  if (isError || !lot) {
    return (
      <div className="flex flex-col gap-4">
        <Button
          variant="ghost"
          className="self-start"
          onClick={() => navigate(paths.lots)}
        >
          <ArrowLeft className="size-4" />
          {t("detail.back")}
        </Button>
        <PageHeader title={t("detail.notFound")} />
      </div>
    )
  }

  const tone = lotStatusTone(lot.status)
  const usinageNumber = lotUsinageNumber(lot)
  const sourceCount = (lot.sources ?? []).length

  return (
    <div className="flex flex-col gap-5">
      <Button
        variant="ghost"
        size="sm"
        className="self-start"
        onClick={() => navigate(paths.lots)}
      >
        <ArrowLeft className="size-4" />
        {t("detail.back")}
      </Button>

      <header className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <h1 className="font-mono text-xl font-semibold tracking-tight">
          {lot.lotNumber}
        </h1>
        {usinageNumber ? (
          <button
            type="button"
            onClick={() =>
              lot.usinageId && navigate(paths.usinageDetail(lot.usinageId))
            }
            className="text-muted-foreground hover:text-foreground inline-flex items-baseline gap-1 text-xs underline-offset-2 hover:underline"
          >
            <span>{t("detail.fromUsinage")}</span>
            <span className="font-mono">{usinageNumber}</span>
          </button>
        ) : null}
        <StatusBadge tone={tone}>{t(`status.${lot.status}`)}</StatusBadge>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCompositionOpen(true)}
            disabled={sourceCount === 0}
          >
            <Layers className="size-4" />
            {t("detail.sourceLoads.button", { count: sourceCount })}
          </Button>
        </div>
      </header>

      <p className="text-muted-foreground -mt-2 text-xs">
        {t("detail.purpose")}
      </p>

      <LotStatusActionCard lot={lot} />

      <QCSection lot={lot} />

      <LotCompositionDialog
        open={compositionOpen}
        onOpenChange={setCompositionOpen}
        lot={lot}
      />
    </div>
  )
}
